import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { ChefHat, Wheat, Milk, Egg, Fish, Leaf, Pencil as Edit2, Save, X, Plus, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useGamification } from '../contexts/GamificationContext';
import { supabase } from '../lib/supabase';

interface MenuItemPadrao {
  data: string;              // dd/MM/yyyy
  dia_semana: string;
  proteina: string[];
  acompanhamentos: string[];
  saladas: string[];
  sobremesa: string;
}

export const Cardapio: React.FC = () => {
  const { user, isAdmin: verificarAdmin } = useAuth();
  const { addActivity } = useGamification();

  const [cardapioPadrao, setCardapioPadrao] = useState<MenuItemPadrao[]>([]);
  const [refMonth, setRefMonth] = useState<Date>(new Date()); // mês que a UI deve renderizar
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<MenuItemPadrao | null>(null);

  const isAdmin = !!user && verificarAdmin();

  // Gera os dias úteis do mês de referência (ignora sáb e dom)
  const diasUteis = useMemo(() => {
    const start = startOfMonth(refMonth);
    const end = endOfMonth(refMonth);
    return eachDayOfInterval({ start, end }).filter(d => {
      const wd = d.getDay();
      return wd !== 0 && wd !== 6;
    });
  }, [refMonth]);

  // Ícones de alérgenos (já existentes — mantidos para compat)
  const alergenos = {
    gluten: { icon: Wheat, name: 'Glúten', color: 'text-yellow-600' },
    lactose: { icon: Milk, name: 'Lactose', color: 'text-blue-600' },
    ovo: { icon: Egg, name: 'Ovo', color: 'text-orange-600' },
    peixe: { icon: Fish, name: 'Peixe', color: 'text-cyan-600' },
    vegetariano: { icon: Leaf, name: 'Vegetariano', color: 'text-green-600' },
  };

  // Busca cardápio do mês atual do Supabase
  useEffect(() => {
    const fetchCardapio = async () => {
      try {
        const inicioMes = format(startOfMonth(refMonth), 'yyyy-MM-dd');
        const fimMes = format(endOfMonth(refMonth), 'yyyy-MM-dd');

        const { data, error } = await supabase
          .from('cardapio_itens')
          .select('*')
          .gte('data', inicioMes)
          .lte('data', fimMes)
          .eq('publicado', true)
          .order('data', { ascending: true });

        if (error) throw error;

        if (!data || data.length === 0) {
          console.log('[CARDAPIO] Nenhum cardápio encontrado para este mês.');
          setCardapioPadrao([]);
          return;
        }

        const loaded: MenuItemPadrao[] = data.map(item => ({
          data: format(new Date(item.data + 'T00:00:00'), 'dd/MM/yyyy'),
          dia_semana: item.dia_semana,
          proteina: item.proteinas || [],
          acompanhamentos: item.acompanhamentos || [],
          saladas: item.saladas || [],
          sobremesa: item.sobremesa || ''
        }));

        console.log('[CARDAPIO] Cardápio carregado do Supabase, itens:', loaded.length);
        setCardapioPadrao(loaded);
      } catch (err) {
        console.error('Erro ao carregar cardápio:', err);
        toast.error('Erro ao carregar cardápio.');
        setCardapioPadrao([]);
      }
    };

    fetchCardapio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refMonth]);

  const handlePreviousMonth = () => {
    setRefMonth(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setRefMonth(prev => addMonths(prev, 1));
  };

  const handleCurrentMonth = () => {
    setRefMonth(new Date());
  };

  const handleEdit = (item: MenuItemPadrao) => {
    setEditingDate(item.data);
    setEditForm({ ...item });
  };

  const handleCancelEdit = () => {
    setEditingDate(null);
    setEditForm(null);
  };

  const handleSave = async () => {
    if (!editForm) return;

    try {
      const [dd, mm, yyyy] = editForm.data.split('/');
      const dateISO = `${yyyy}-${mm}-${dd}`;

      // Verifica se já existe um registro para esta data
      const { data: existing, error: checkError } = await supabase
        .from('cardapio_itens')
        .select('id')
        .eq('data', dateISO)
        .eq('tipo', 'padrao')
        .maybeSingle();

      if (checkError) throw checkError;

      const payload = {
        data: dateISO,
        tipo: 'padrao',
        dia_semana: editForm.dia_semana,
        proteinas: editForm.proteina,
        acompanhamentos: editForm.acompanhamentos,
        saladas: editForm.saladas,
        sobremesa: editForm.sobremesa,
        publicado: true,
      };

      if (existing) {
        // Atualiza registro existente
        const { error } = await supabase
          .from('cardapio_itens')
          .update(payload)
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        // Cria novo registro
        const { error } = await supabase
          .from('cardapio_itens')
          .insert(payload);

        if (error) throw error;
      }

      // Mantém as trocas já feitas para este dia com a proteína atual do cardápio
      const { error: erroTrocas } = await supabase
        .from('trocas_proteina')
        .update({ proteina_original: editForm.proteina.join(' + ') })
        .eq('data_troca', dateISO);
      if (erroTrocas) console.warn('[CARDAPIO] Trocas do dia nao atualizadas:', erroTrocas);

      // Atualiza o estado local
      const updatedCardapio = cardapioPadrao.map(item =>
        item.data === editForm.data ? editForm : item
      );

      // Se não existia, adiciona
      if (!cardapioPadrao.find(item => item.data === editForm.data)) {
        updatedCardapio.push(editForm);
      }

      setCardapioPadrao(updatedCardapio);
      toast.success('Cardápio atualizado com sucesso!');
      handleCancelEdit();
    } catch (error: any) {
      console.error('[CARDAPIO] Erro ao salvar:', error);
      toast.error('Erro ao salvar cardápio: ' + (error.message || 'Tente novamente'));
    }
  };

  const addItemToArray = (field: 'proteina' | 'acompanhamentos' | 'saladas', value: string) => {
    if (!editForm || !value.trim()) return;
    setEditForm({
      ...editForm,
      [field]: [...editForm[field], value.trim()],
    });
  };

  const removeItemFromArray = (field: 'proteina' | 'acompanhamentos' | 'saladas', index: number) => {
    if (!editForm) return;
    setEditForm({
      ...editForm,
      [field]: editForm[field].filter((_, i) => i !== index),
    });
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h1 className="font-display text-headline-lg text-on-surface mb-1">Cardapio</h1>
            <p className="text-body-md text-secondary">Confira o cardapio do restaurante para o mes selecionado.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 bg-white border border-outline-variant rounded-xl px-4 py-2 shadow-sm">
              <button
                onClick={handlePreviousMonth}
                className="p-1.5 hover:bg-surface-container rounded-lg transition-colors"
                title="Mes anterior"
              >
                <ChevronLeft className="w-5 h-5 text-secondary" />
              </button>
              <button
                onClick={handleCurrentMonth}
                className="px-4 py-1 text-sm font-semibold text-on-surface hover:bg-surface-container rounded-lg transition-colors capitalize min-w-[140px] text-center"
                title="Voltar para o mes atual"
              >
                {format(refMonth, 'MMMM yyyy', { locale: ptBR })}
              </button>
              <button
                onClick={handleNextMonth}
                className="p-1.5 hover:bg-surface-container rounded-lg transition-colors"
                title="Proximo mes"
              >
                <ChevronRight className="w-5 h-5 text-secondary" />
              </button>
            </div>
          </div>
        </div>

        {cardapioPadrao.length === 0 && (
          <div className="bg-white dark:bg-gray-800border border-outline-variant rounded-xl p-12 text-center">
            <ChefHat className="w-16 h-16 mx-auto text-secondary/40 mb-4" />
            <h3 className="text-headline-md text-on-surface mb-2">
              Nenhum cardapio disponivel para {format(refMonth, 'MMMM yyyy', { locale: ptBR })}
            </h3>
            <p className="text-secondary mb-6">
              O cardapio deste mes ainda nao foi cadastrado.
            </p>
            <button
              onClick={handleCurrentMonth}
              className="bg-primary text-white px-6 py-2.5 rounded-lg hover:bg-primary-dark transition-colors font-bold"
            >
              Voltar para o mes atual
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {diasUteis.map(date => {
            const dataStr = format(date, 'dd/MM/yyyy');
            const item = cardapioPadrao.find(c => c.data === dataStr);
            const isEditing = editingDate === dataStr;
            const isToday = format(new Date(), 'dd/MM/yyyy') === dataStr;

            if (!item && !isAdmin) {
              return (
                <div key={dataStr} className="bg-white dark:bg-gray-800border border-outline-variant rounded-xl p-5 text-secondary italic">
                  <div className="bg-secondary-container text-on-surface px-3 py-1 rounded-lg text-sm font-semibold w-fit mb-3">
                    {dataStr}
                  </div>
                  Cardapio nao disponivel para este dia.
                </div>
              );
            }

            if (isEditing && editForm) {
              return (
                <div key={dataStr} className="bg-white dark:bg-gray-800rounded-xl shadow-lg border-2 border-primary p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="bg-primary text-white px-3 py-1 rounded-lg text-sm font-bold">
                      {editForm.data}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSave}
                        className="p-1.5 text-primary hover:bg-primary/10 rounded-lg"
                        title="Salvar"
                      >
                        <Save className="w-5 h-5" />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1.5 text-error hover:bg-error-container/30 rounded-lg"
                        title="Cancelar"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3 text-sm">
                    <div>
                      <label className="font-semibold text-on-surface block mb-1">Proteinas:</label>
                      {editForm.proteina.map((prot, idx) => (
                        <div key={idx} className="flex items-center space-x-2 mb-1">
                          <input
                            type="text"
                            value={prot}
                            onChange={(e) => {
                              const updated = [...editForm.proteina];
                              updated[idx] = e.target.value;
                              setEditForm({ ...editForm, proteina: updated });
                            }}
                            className="flex-1 px-3 py-1.5 border border-outline-variant rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          />
                          <button
                            onClick={() => removeItemFromArray('proteina', idx)}
                            className="text-error hover:text-error/80"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addItemToArray('proteina', 'Nova proteina')}
                        className="text-primary hover:text-primary-dark text-xs flex items-center mt-1 font-semibold"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Adicionar
                      </button>
                    </div>

                    <div>
                      <label className="font-semibold text-on-surface block mb-1">Acompanhamentos:</label>
                      {editForm.acompanhamentos.map((acomp, idx) => (
                        <div key={idx} className="flex items-center space-x-2 mb-1">
                          <input
                            type="text"
                            value={acomp}
                            onChange={(e) => {
                              const updated = [...editForm.acompanhamentos];
                              updated[idx] = e.target.value;
                              setEditForm({ ...editForm, acompanhamentos: updated });
                            }}
                            className="flex-1 px-3 py-1.5 border border-outline-variant rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          />
                          <button
                            onClick={() => removeItemFromArray('acompanhamentos', idx)}
                            className="text-error hover:text-error/80"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addItemToArray('acompanhamentos', 'Novo acompanhamento')}
                        className="text-primary hover:text-primary-dark text-xs flex items-center mt-1 font-semibold"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Adicionar
                      </button>
                    </div>

                    <div>
                      <label className="font-semibold text-on-surface block mb-1">Saladas:</label>
                      {editForm.saladas.map((salada, idx) => (
                        <div key={idx} className="flex items-center space-x-2 mb-1">
                          <input
                            type="text"
                            value={salada}
                            onChange={(e) => {
                              const updated = [...editForm.saladas];
                              updated[idx] = e.target.value;
                              setEditForm({ ...editForm, saladas: updated });
                            }}
                            className="flex-1 px-3 py-1.5 border border-outline-variant rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          />
                          <button
                            onClick={() => removeItemFromArray('saladas', idx)}
                            className="text-error hover:text-error/80"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addItemToArray('saladas', 'Nova salada')}
                        className="text-primary hover:text-primary-dark text-xs flex items-center mt-1 font-semibold"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Adicionar
                      </button>
                    </div>

                    <div>
                      <label className="font-semibold text-on-surface block mb-1">Sobremesa:</label>
                      <input
                        type="text"
                        value={editForm.sobremesa}
                        onChange={(e) => setEditForm({ ...editForm, sobremesa: e.target.value })}
                        className="w-full px-3 py-1.5 border border-outline-variant rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                  </div>
                </div>
              );
            }

            const displayItem = item || {
              data: dataStr,
              dia_semana: format(date, 'EEEE', { locale: ptBR }),
              proteina: [],
              acompanhamentos: [],
              saladas: [],
              sobremesa: '',
            };

            return (
              <div key={displayItem.data} className={`bg-white dark:bg-gray-800 rounded-xl border overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
                isToday ? 'border-primary/30 ring-1 ring-primary/20' : 'border-outline-variant'
              }`}>
                {isToday && <div className="h-1 bg-primary" />}
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`px-3 py-1 rounded-lg text-sm font-bold ${
                      isToday ? 'bg-primary text-white' : 'bg-primary/10 text-primary'
                    }`}>
                      {displayItem.data}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-label-md text-secondary capitalize">{displayItem.dia_semana}</span>
                      {isAdmin && (
                        <button
                          onClick={() => handleEdit(displayItem)}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors"
                          title="Editar cardapio do dia"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                          Editar
                        </button>
                      )}
                    </div>
                  </div>

                  {displayItem.proteina.length > 0 ? (
                    <>
                      <h3 className="font-display font-bold text-on-surface mb-3 text-lg leading-tight">{displayItem.proteina.join(' + ')}</h3>

                      <div className="space-y-2.5 text-sm">
                        {displayItem.acompanhamentos.length > 0 && (
                          <div>
                            <p className="font-semibold text-on-surface text-xs uppercase tracking-wider mb-1">Acompanhamentos</p>
                            <ul className="text-secondary space-y-0.5">
                              {displayItem.acompanhamentos.map((acomp, idx) => (
                                <li key={idx} className="flex items-center gap-1.5">
                                  <span className="w-1 h-1 bg-primary rounded-full" />
                                  {acomp}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {displayItem.saladas.length > 0 && (
                          <div>
                            <p className="font-semibold text-on-surface text-xs uppercase tracking-wider mb-1">Saladas</p>
                            <p className="text-secondary">{displayItem.saladas.join(', ')}</p>
                          </div>
                        )}

                        {displayItem.sobremesa && (
                          <div>
                            <p className="font-semibold text-on-surface text-xs uppercase tracking-wider mb-1">Sobremesa</p>
                            <p className="text-secondary">{displayItem.sobremesa}</p>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-secondary italic">Cardapio nao disponivel</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};
